import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import ConnectionStatus from './components/ConnectionStatus';
import OfflineQueueManager from './components/OfflineQueueManager';
import NotificationQuickButton from './components/NotificationQuickButton';
import AppToastCenter from './components/AppToastCenter';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import GlobalQuickMenu from './components/GlobalQuickMenu';
import PushNotificationControl from './components/PushNotificationControl';
import MobileBottomNav from './components/MobileBottomNav';
import { apiFetch } from './api';
import { getUser } from './auth';

import Login from './pages/Login';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Stores from './pages/Stores';
import Wallets from './pages/Wallets';
import Transactions from './pages/Transactions';
import Employees from './pages/Employees';
import AuditLog from './pages/AuditLog';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import UserBalance from './pages/UserBalance';
import MasrafiRequests from './pages/MasrafiRequests';
import Archive from './pages/Archive';
import StoreDebtTransfer from './pages/StoreDebtTransfer';

import './App.css';

function GlobalBrandMark() {
  const location = useLocation();

  if (location.pathname === '/login') {
    return null;
  }

  return (
    <button
      type="button"
      className="global-brand-mark"
      title="نظام الإشارة الإداري"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <img src="/system-logo.jpeg" alt="شعار النظام" />
    </button>
  );
}

function ActivityTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/login') return;

    const user = getUser();
    if (!user) return;

    const sendHeartbeat = () => {
      apiFetch('/activity/heartbeat', {
        method: 'POST',
        headers: {
          'X-Active-Page': location.pathname
        },
        body: JSON.stringify({
          page: location.pathname,
          device_info: navigator.userAgent
        })
      }).catch(() => {});
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000);
    window.addEventListener('focus', sendHeartbeat);
    document.addEventListener('visibilitychange', sendHeartbeat);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sendHeartbeat);
      document.removeEventListener('visibilitychange', sendHeartbeat);
    };
  }, [location.pathname]);

  return null;
}

function FloatingMobileActions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showTop, setShowTop] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 350);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (location.pathname === '/login') return null;

  return (
    <div className="fixed bottom-4 left-4 z-[9999] flex flex-col gap-2 md:hidden print:hidden">
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-12 h-12 rounded-full bg-gray-900 text-white shadow-xl border border-white/40 font-black text-lg"
          aria-label="الصعود للأعلى"
          title="أعلى الصفحة"
        >
          ↑
        </button>
      )}
      {location.pathname !== '/' && (
        <button
          type="button"
          onClick={() => navigate('/')}
          className="w-12 h-12 rounded-full bg-orange-600 text-white shadow-xl border border-white/40 font-black text-lg"
          aria-label="الرجوع للرئيسية"
          title="الرئيسية"
        >
          🏠
        </button>
      )}
    </div>
  );
}

function AppRoutes() {
  return (
    <>
      <ActivityTracker />
      <ConnectionStatus />
      <OfflineQueueManager />
      <NotificationQuickButton />
      <AppToastCenter />
      <PwaInstallPrompt />
      <PushNotificationControl />
      <GlobalQuickMenu />
      <GlobalBrandMark />
      <FloatingMobileActions />
      <MobileBottomNav />

      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/stores" element={<ProtectedRoute><Stores /></ProtectedRoute>} />
        <Route path="/wallets" element={<ProtectedRoute><Wallets /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/store-debt-transfer" element={<ProtectedRoute><StoreDebtTransfer /></ProtectedRoute>} />
        <Route path="/masrafi-requests" element={<ProtectedRoute><MasrafiRequests /></ProtectedRoute>} />
        <Route path="/archive" element={<ProtectedRoute><Archive /></ProtectedRoute>} />
        <Route path="/user-balance" element={<ProtectedRoute><UserBalance /></ProtectedRoute>} />
        <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
        <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />

        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

        <Route
          path="*"
          element={
            <div dir="rtl" className="brand-not-found">
              <img src="/system-logo.jpeg" alt="شعار النظام" />
              <h1>الصفحة غير موجودة</h1>
              <p>المسار الحالي: {window.location.pathname}</p>
            </div>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
